import type { CurrentUser, KycApplication, KycDocumentKind, UserProfile } from '@app/shared'

import { ApiError, NetworkError, api, type ApiErrorBody } from '../lib/http'
import { getAccessToken } from '../lib/session'

/**
 * Mirrors `lib/http.ts`: same origin in dev and in production behind one
 * ingress, an absolute origin only when the API genuinely lives elsewhere.
 */
const BASE_URL = `${import.meta.env.VITE_API_URL ?? ''}/api/v1`

export function getCurrentUser(): Promise<CurrentUser> {
  return api.get<CurrentUser>('/users/me')
}

export type ProfilePatch = Partial<
  Pick<
    UserProfile,
    'firstName' | 'lastName' | 'middleName' | 'nickname' | 'phone' | 'countryCode' | 'language'
  >
>

export function updateProfile(patch: ProfilePatch): Promise<CurrentUser> {
  return api.patch<CurrentUser>('/users/me', patch)
}

export interface KycSubmission {
  documentType: 'passport' | 'id_card' | 'driver_license'
  documentNumber: string
  documentCountry: string
  /** Both dates are required by `submitApplicationSchema`; neither is optional. */
  documentIssueDate: string
  documentExpiryDate: string
  firstName: string
  lastName: string
  middleName?: string
  dateOfBirth: string
}

export function submitKyc(submission: KycSubmission): Promise<KycApplication> {
  return api.post<KycApplication>('/kyc/applications', submission)
}

export async function getMyKyc(): Promise<KycApplication | null> {
  // 404 here means "nothing submitted yet", which is the empty state the KYC
  // screen renders as its intro form — not a failure to surface.
  try {
    return await api.get<KycApplication | null>('/kyc/applications/me')
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null
    throw error
  }
}

/** One scan on its way to `POST /kyc/applications/:id/documents`. */
export interface KycUpload {
  kind: KycDocumentKind
  file: File
}

/**
 * The multipart endpoint: the form field name IS the document kind, so multer
 * rejects an unknown field before a single byte is buffered.
 *
 * This is the one call in the client that does not go through `lib/http.ts`.
 * That client serialises every body with `JSON.stringify`, which turns a
 * `FormData` into `{}` and drops the boundary header the server parses the
 * upload with — so the request is issued here against the same base URL and the
 * same access token instead, and fails with the same `ApiError` everything else
 * throws.
 */
export async function uploadKycDocuments(
  applicationId: string,
  documents: readonly KycUpload[],
): Promise<KycApplication> {
  const form = new FormData()
  for (const { kind, file } of documents) {
    form.append(kind, file, file.name)
  }

  const token = getAccessToken()
  let response: Response
  try {
    response = await fetch(`${BASE_URL}/kyc/applications/${applicationId}/documents`, {
      method: 'POST',
      // No content-type header: the browser has to set the multipart boundary.
      headers: token === null ? {} : { authorization: `Bearer ${token}` },
      body: form,
    })
  } catch {
    throw new NetworkError()
  }

  const text = await response.text()
  const parsed: unknown = text === '' ? null : JSON.parse(text)

  if (!response.ok) {
    throw new ApiError(response.status, parsed as ApiErrorBody | null, response.statusText)
  }
  return parsed as KycApplication
}
